import {
  Show,
  SimpleShowLayout,
  TextField,
  BooleanField,
  TopToolbar,
  EditButton,
  CloneButton,
  useRecordContext,
  ResourceContextProvider,
  useRedirect,
} from 'react-admin';
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { getFieldTypeDisplayLabel } from './types';

const FormShowActions = () => (
  <TopToolbar>
    <EditButton />
    <CloneButton />
  </TopToolbar>
);

const ConfigurationBreadcrumbs = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  return (
    <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, mb: 2 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
        <Link
          underline="hover"
          component="button"
          variant="body1"
          onClick={() => redirect('/configuration')}
          sx={{ cursor: 'pointer' }}
        >
          Configuration
        </Link>
        <Link
          underline="hover"
          component="button"
          variant="body1"
          onClick={() => redirect('/configuration/forms')}
          sx={{ cursor: 'pointer' }}
        >
          Forms
        </Link>
        <Typography color="text.primary">{record?.name || 'Form'}</Typography>
      </Breadcrumbs>
    </Box>
  );
};

const FormInputOptionsListField = ({
  label,
  optionsList,
}: {
  label: string;
  optionsList?: string[];
}) => (
  <>
    {label}:
    <ul>
      {optionsList?.map((option) => (
        <li key={option}>{option}</li>
      ))}
    </ul>
  </>
);

const FormInputField = ({ record }: { record: { type: string; options?: string[] } }) => {
  switch (record.type) {
    case 'Text':
      return <>Text entry field</>;
    case 'Number':
      return <>Number entry field</>;
    case 'Notes':
      return <>Multiline note field</>;
    case 'RadioButtons':
      return (
        <FormInputOptionsListField label="Radio buttons" optionsList={record.options} />
      );
    case 'Checkboxes':
      return (
        <FormInputOptionsListField label="Checkboxes" optionsList={record.options} />
      );
    case 'Dropdown':
      return (
        <FormInputOptionsListField label="Dropdown" optionsList={record.options} />
      );
    case 'YesNo':
      return <>Yes / No field</>;
    case 'Date':
      return <>Date field</>;
    case 'Time':
      return <>Time field</>;
    case 'Signature':
      return <>{getFieldTypeDisplayLabel('Signature')} field</>;
    case 'SignatureImage':
      return <>{getFieldTypeDisplayLabel('SignatureImage')} field</>;
    case 'VehicleLookup':
      return <>Vehicle selection</>;
    case 'TrailerLookup':
      return <>Trailer selection</>;
    case 'EquipmentLookup':
      return <>Equipment selection</>;
    case 'Information':
      return <>Section header</>;
    case 'Images':
      return <>Image upload</>;
    case 'HtmlDisplay':
      return <>HTML display</>;
    default:
      return <>{record.type}</>;
  }
};

const FormDefinitionPreview = () => {
  const record = useRecordContext();
  if (!record?.formDefinition) return null;

  let form: { fields?: Array<{ type: string; label: string; options?: string[] }> };
  try {
    form =
      typeof record.formDefinition === 'string'
        ? JSON.parse(record.formDefinition)
        : record.formDefinition;
  } catch {
    return <Typography color="error">Invalid form definition JSON</Typography>;
  }

  if (!form?.fields?.length) {
    return <Typography color="text.secondary">No fields</Typography>;
  }

  return (
    <Box sx={{ mt: 1 }}>
      {form.fields.map((field, i) => (
        <Box key={i} sx={{ mb: 2 }}>
          <Typography fontWeight="bold">{field.label}</Typography>
          <Box sx={{ ml: 1, fontSize: 14, fontStyle: 'italic' }}>
            <FormInputField record={field} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const FormTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span>{record.name}</span>;
};

export const FormsShow = () => (
  <ResourceContextProvider value="forms">
    <Show title={<FormTitle />} actions={<FormShowActions />}>
      <ConfigurationBreadcrumbs />
      <SimpleShowLayout>
        <TextField source="name" />
        <BooleanField source="active" />
        <Box sx={{ mt: 1 }}>
          Users notified when completed:
        </Box>
        <TextField source="emails" label={false} emptyText="None" />
        <Box sx={{ mt: 2, fontWeight: 'bold' }}>Fields preview</Box>
        <FormDefinitionPreview />
      </SimpleShowLayout>
    </Show>
  </ResourceContextProvider>
);
